import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["image", "status", "address", "tabs", "text", "start", "sessionControl", "alert", "alertText"]
  static values = { url: String }

  connect() { this.request("status") }
  disconnect() {
    clearTimeout(this.timer)
    clearTimeout(this.typingTimer)
    this.abort?.abort()
    this.commandQueue?.forEach((command) => command.resolve(false))
  }

  async request(action, values = {}) {
    clearTimeout(this.timer)
    if (!this.element.isConnected) return false
    if (this.busy) {
      if (action === "status") return false
      this.commandQueue ||= []
      if (this.commandQueue.length >= 10) return false
      return new Promise((resolve) => this.commandQueue.push({ action, values, resolve }))
    }
    this.busy = true
    this.element.setAttribute("aria-busy", "true")
    this.imageTarget.style.pointerEvents = action === "status" ? "" : "none"
    this.abort = new AbortController()
    const timeout = setTimeout(() => this.abort.abort(), 50000)
    let ok = false
    try {
      const response = await fetch(this.urlValue, { method: "POST", credentials: "same-origin", cache: "no-store", headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" }, body: JSON.stringify({ action_kind: action, ...values }), signal: this.abort.signal })
      if ([401, 403, 409].includes(response.status)) {
        this.denied = true
        this.active = false
        this.imageTarget.removeAttribute("src")
        this.imageTarget.hidden = true
        this.tabsTarget.replaceChildren()
        this.tabRenderKey = null
        this.alertTarget.hidden = true
        this.statusTarget.textContent = "Доступ к удалённому браузеру закрыт. Войдите заново или обратитесь к администратору."
        return false
      }
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Нет соединения с Firefox")
      if (data.state === "invalid") {
        this.statusTarget.textContent = data.message
        return false
      }
      const wasActive = this.active
      this.active = ["active", "alert"].includes(data.state)
      this.alertTarget.hidden = data.state !== "alert"
      this.alertTextTarget.textContent = data.alert || ""
      this.startTarget.hidden = this.active
      if (this.active) {
        this.renderTabs(data)
        this.statusTarget.textContent = data.message || (data.state === "alert" ? "Firefox ожидает вашего подтверждения." : `Приватная сессия · до закрытия без действий: ${Math.ceil((data.idle_seconds || 1800) / 60)} мин.`)
        // Background frame updates must never overwrite an address being typed.
        if (action !== "status" && data.url && !this.addressDirty && document.activeElement !== this.addressTarget) this.addressTarget.value = data.url === "about:blank" ? "" : data.url
        if (data.image) {
          this.width = data.width
          this.height = data.height
          this.imageTarget.src = `data:image/png;base64,${data.image}`
        }
        this.imageTarget.hidden = !data.image
      } else {
        if (wasActive || action === "close") {
          this.addressTarget.value = ""
          this.addressDirty = false
          this.textTarget.value = ""
        }
        this.inputQueue = []
        clearTimeout(this.typingTimer)
        this.imageTarget.hidden = true
        this.imageTarget.removeAttribute("src")
        this.tabsTarget.replaceChildren()
        this.tabRenderKey = null
        this.statusTarget.textContent = "Приватная сессия закрыта. Вкладки и временные данные удалены. Можно открыть новую сессию."
      }
      ok = true
    } catch (error) {
      if (this.element.isConnected) this.statusTarget.textContent = "Нет соединения с Firefox, машина занята или VPN недоступен. Действие не повторялось автоматически."
    } finally {
      clearTimeout(timeout)
      this.busy = false
      this.element.setAttribute("aria-busy", "false")
      this.imageTarget.style.pointerEvents = ""
      this.startTarget.disabled = this.denied === true
      this.sessionControlTargets.forEach((button) => { button.disabled = !this.active || this.denied === true })
      const next = this.commandQueue?.shift()
      if (next && ok && this.element.isConnected) queueMicrotask(() => this.request(next.action, next.values).then(next.resolve))
      else {
        next?.resolve(false)
        this.commandQueue?.forEach((command) => command.resolve(false))
        this.commandQueue = []
        if (!this.denied && this.element.isConnected && (this.active || !ok)) this.timer = setTimeout(() => { if (document.hidden) this.timer = setTimeout(() => this.request("status"), 3000); else this.request("status") }, 2500)
      }
    }
    return ok
  }

  renderTabs(data) {
    if (!data.tabs) return
    const renderKey = JSON.stringify([data.tabs, data.tab])
    if (renderKey === this.tabRenderKey) return
    this.tabRenderKey = renderKey
    this.tabsTarget.replaceChildren()
    data.tabs.forEach((tab) => {
      const group = document.createElement("div")
      group.className = "btn-group btn-group-sm"
      const button = document.createElement("button")
      button.type = "button"
      button.className = `btn ${tab.id === data.tab ? "btn-primary" : "btn-outline-primary"}`
      button.textContent = tab.title.slice(0, 60)
      Object.assign(button.style, { maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })
      button.addEventListener("click", () => this.request("switch_tab", { tab: tab.id }))
      const close = document.createElement("button")
      close.type = "button"
      close.className = "btn btn-outline-secondary"
      close.textContent = "×"
      close.setAttribute("aria-label", `Закрыть вкладку ${tab.title}`)
      close.addEventListener("click", () => this.request("close_tab", { tab: tab.id }))
      group.append(button, close)
      this.tabsTarget.append(group)
    })
  }

  start() { this.request("start") }
  addressChanged() { this.addressDirty = true }
  async navigate(event) {
    event.preventDefault()
    let url = this.addressTarget.value.trim()
    if (!url) return
    const draft = url
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`
    if (!this.active && !await this.request("start")) return
    const ok = await this.request("navigate", { browser_url: url })
    if (ok && [draft, url].includes(this.addressTarget.value.trim())) this.addressDirty = false
  }
  close() { this.request("close") }
  newTab() { this.request("new_tab") }
  reload() { this.request("reload") }
  back() { this.request("back") }
  forward() { this.request("forward") }
  scroll(event) { this.request("scroll", { delta: Number(event.currentTarget.dataset.delta) }) }
  wheel(event) {
    if (!this.active || !this.width || !event.deltaY) return
    event.preventDefault()
    const bounds = this.imageTarget.getBoundingClientRect()
    this.request("scroll", { delta: Math.sign(event.deltaY) * Math.min(800, Math.max(50, Math.round(Math.abs(event.deltaY)))), x: (event.clientX - bounds.left) * this.width / bounds.width, y: (event.clientY - bounds.top) * this.height / bounds.height })
  }
  enlarge() { this.imageTarget.style.minWidth = this.imageTarget.style.minWidth ? "" : "1100px" }
  acceptAlert() { this.request("alert_accept") }
  dismissAlert() { this.request("alert_dismiss") }
  key(event) { this.request("key", { key: event.currentTarget.dataset.key }) }
  click(event) {
    if (!this.imageTarget.complete || !this.width) return
    this.imageTarget.focus({ preventScroll: true })
    const bounds = this.imageTarget.getBoundingClientRect()
    this.request("click", { x: (event.clientX - bounds.left) * this.width / bounds.width, y: (event.clientY - bounds.top) * this.height / bounds.height })
  }
  type(event) {
    event.preventDefault()
    const text = this.textTarget.value
    if (text) {
      this.textTarget.value = ""
      this.request("type", { text }).then((ok) => { if (!ok && !this.textTarget.value) this.textTarget.value = text })
    }
  }
  queueText(text) {
    this.inputQueue ||= []
    const last = this.inputQueue[this.inputQueue.length - 1]
    if (last?.action === "type") last.values.text = (last.values.text + text).slice(0, 4000)
    else this.inputQueue.push({ action: "type", values: { text: text.slice(0, 4000) } })
    clearTimeout(this.typingTimer)
    this.typingTimer = setTimeout(() => this.flushText(), 200)
  }
  async flushText() {
    if (!this.element.isConnected || !this.active) return
    if (this.busy) { this.typingTimer = setTimeout(() => this.flushText(), 100); return }
    const command = this.inputQueue?.shift()
    if (command) {
      const ok = await this.request(command.action, command.values)
      if (!ok) this.inputQueue = []
      else if (this.inputQueue.length) this.typingTimer = setTimeout(() => this.flushText(), 0)
    }
  }
  keyboard(event) {
    if (event.isComposing) return
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
      event.preventDefault()
      this.inputQueue ||= []
      this.inputQueue.push({ action: "key", values: { key: "Control+a" } })
      this.flushText()
      return
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key.length === 1) { event.preventDefault(); this.queueText(event.key) }
    else if (["Enter", "Tab", "Backspace", "Delete", "Escape", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"].includes(event.key)) {
      event.preventDefault()
      this.inputQueue ||= []
      this.inputQueue.push({ action: "key", values: { key: event.key } })
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => this.flushText(), 0)
    }
  }
  paste(event) { event.preventDefault(); this.queueText(event.clipboardData.getData("text/plain")) }
  composed(event) { if (event.data) this.queueText(event.data) }
}
